import { useEffect, useMemo, useState, useCallback } from 'react';
import OLVectorLayer from 'ol/layer/Vector';
import {
  Style,
  Stroke,
  Icon,
  Fill,
  Text,
  RegularShape,
} from 'ol/style';
import Overlay from 'ol/Overlay';
import Circle from 'ol/style/Circle';
import * as d3 from 'd3';
import * as d3ColorScale from 'd3-scale-chromatic';
import { unByKey } from 'ol/Observable';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
// import { rgba } from '../../MapMixer/MapOptions/ColorPicker';
import { numberFormatter, convertRGBtoRGBA } from '../Helpers';
import { vector } from '../Source';
import { rgba } from '../../MapMixer/MapOptions/ColorPicker';
import capital from '../assets/map-icons/capital.svg';
import city from '../assets/map-icons/city.svg';
import settlement from '../assets/map-icons/settlement.svg';
import marker from '../assets/map-icons/marker.svg';
import airport from '../assets/map-icons/airport.svg';
import borderCrossing from '../assets/map-icons/borderCrossing.svg';
import borderCrossingActive from '../assets/map-icons/borderCrossingActive.svg';
import borderCrossingPotential from '../assets/map-icons/borderCrossingPotential.svg';
import triangle from '../assets/map-icons/triangle.svg';
import conflict from '../assets/map-icons/Conflict.svg';
import idpRefugeeCamp from '../assets/map-icons/idp-refugee-camp.svg';
import epicentre from '../assets/map-icons/epicentre.svg';
import office from '../assets/map-icons/Group.svg';
import down from '../../MapMixer/assets/down.svg';
import up from '../../MapMixer/assets/up.svg';

const getFontFamily = (locationType = null) => {
  const root = document.documentElement;
  
  // Use primary font for view labels, secondary for others
  const isViewLabel = locationType === 'view' || locationType === 'capital';
  const fontFamily = isViewLabel 
    ? getComputedStyle(root).getPropertyValue('--font-family-heading').trim() ||
      getComputedStyle(root).getPropertyValue('--org-primary-font-family').trim()
    : getComputedStyle(root).getPropertyValue('--font-family-base').trim() ||
      getComputedStyle(root).getPropertyValue('--org-secondary-font-family').trim();
      
  return fontFamily || "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
};

const SymbolLayer = ({
  map,
  data,
  symbol = 'circle',
  zIndex = 1,
  opacity = 1,
  showLabels = false,
  labelColumn = '',
  labelStyle = '',
  scale = 1,
  scaleType = 'fixed',
  scaleScaling = 'flannery',
  scaleColumn = '',
  scaleDataMin = 0,
  scaleDataMax = 0,
  style = {},
  enableTooltips = false,
  tooltipsTitleColumn = '',
  tooltipsValueColumn = '',
  tooltipsValueLabel = '',
  tooltipsValueColumnSecondary = '',
  tooltipsValueLabelSecondary = '',
  tooltipsDateColumn = '',
  fontVersion = 0, // Add fontVersion prop to trigger font updates
}) => {
  const [symbolLayer, setSymbolLayer] = useState(null);
  const [tooltipListener, setTooltipListener] = useState(undefined);

  // Create a stable layer ID using deterministic values
  const layerId = useMemo(() => {
    if (!data) return `symbol-layer-${symbol}-empty`;
    
    try {
      const dataStr = typeof data === 'object' ? JSON.stringify(data) : String(data);
      const dataHash = dataStr
        .split('')
        .reduce((hash, char) => ((hash << 5) - hash) + char.charCodeAt(0), 0)
        .toString(36);
      return `symbol-layer-${symbol}-${dataHash}`;
    } catch (error) {
      return `symbol-layer-${symbol}-fallback`;
    }
  }, [symbol, data]);

  const symbolIcons = useMemo(() => ({
    capital,
    city,
    settlement,
    'idp-refugee-camp': idpRefugeeCamp,
    airport,
    marker,
    borderCrossing,
    borderCrossingActive,
    borderCrossingPotential,
    triangle,
    conflict,
    epicentre,
    office,
    circle: 'circle',
    location: 'location',
  }), []); // Icons never change, so memoize them

  // OpenLayers provides built-in decluttering for labels
  // We use the 'declutter' option on the vector layer instead of custom positioning

  const createVectorLayer = () => {
    let xOffset = 1;
    if (symbol === 'capital') xOffset = 1.9;

    let points;
    try {
      if (data.type === 'FeatureCollection') {
        points = data.features;
      } else if (Array.isArray(data)) {
        points = data;
      } else {
        return undefined;
      }

      function styleFunction(feature, selected = false) {

        let vectorStyle;
        const properties = feature.getProperties();
        let { strokeWidth } = properties.style;
        let { fillPow } = style;
        let numSteps = style.fillSteps;
        if (style.fillScaleType === 'continuous') numSteps = 50;
        if (style.fillScaleType === 'steps') fillPow = 1;

        if (selected) {
          strokeWidth += 0.5;
        }

        // Check if this is a location type that should use a square symbol
        if (properties.type === 'location') {
          vectorStyle = new Style({
            image: new RegularShape({
              points: 4, // Creates a square
              radius: 4, // Size of the square
              angle: Math.PI / 4, // Rotate 45 degrees to make it a square
              fill: new Fill({
                color: 'rgba(0, 0, 0, 0.8)', // Dark gray/black color
              }),
              stroke: new Stroke({
                color: 'rgba(255, 255, 255, 0.8)', // White border for contrast
                width: 1,
              }),
            }),
          });
        } else if (symbol === 'circle') {
          let fill = new Fill({
            color: rgba(properties.style.fill),
          });

          const colorsArrayPow = d3.scalePow()
            .exponent(fillPow)
            .domain([0, numSteps]);

          const colorScale = d3
            .scaleLinear()
            .range([0, numSteps])
            .domain([style.fillDataMin, style.fillDataMax]);

          let colorStrPow = null;
          if (style.fillType === 'categorical') {
            colorStrPow = null;
          } else {
            colorStrPow = colorsArray.map((c, i) => {
              const colorIndex = Math.ceil(colorsArrayPow(i) * numSteps);
              return (colorsArray[colorIndex]);
            });
          }

          let colorStrPowNegative = null;
          if (style.fillType === 'categorical') {
            colorStrPowNegative = null;
          } else {
            colorStrPowNegative = colorsArrayNegative.map((c, i) => {
              const colorIndex = Math.ceil(colorsArrayPow(i) * numSteps) ;
              return (colorsArrayNegative[colorIndex]);
            });
          }

          if (style.fillType === 'graduated') {
            const val = feature.get(style.fillColumn);
            let colorArrayIndex;
            let polygonColor;
            if (val > 0) {
              colorArrayIndex = Math.floor(colorScale(val)) - 1;
              polygonColor = colorStrPow[colorArrayIndex];
            } else if (val < 0) {
              colorArrayIndex = Math.floor(colorScale(Math.abs(val))) - 1;
              polygonColor = colorStrPowNegative[colorArrayIndex];
            }

            if (typeof polygonColor !== 'undefined') {
              const rgbaColor = convertRGBtoRGBA(polygonColor, 0.5);
              fill = new Fill({
                color: (rgbaColor),
              });
            } else {
              fill = new Fill({
                color: 'transparent',
              });
            }
          }
          if (style.fillType === 'categorical') {
            const polygonColor = colorsArray[feature.get(style.fillColumn)];
            if (typeof polygonColor !== 'undefined') {
              const rgbaColor = convertRGBtoRGBA(polygonColor, 0.5);
              fill = new Fill({
                color: (rgbaColor),
              });
            } else {
              fill = new Fill({
                color: 'transparent',
              });
            }
          }
          vectorStyle = new Style({
            image: new Circle({
              radius: properties.size,
              fill,
              stroke: new Stroke({
                color: rgba(properties.style.stroke),
                width: strokeWidth,
              }),
            }),
          });
        } else {
          // Check if the location type is 'town' and use settlement icon
          const iconSymbol = properties.type === 'town' ? 'settlement' : 
                           properties.type === 'epicentre' ? 'epicentre' : 
                           properties.type === 'office' ? 'office' : // Handle office type
                           properties.type === 'location' ? 'location' :
                           properties.type === 'region' ? 'location' : // Treat regions the same as locations
                           properties.type === 'capital' ? 'capital' : // Handle capital cities
                           symbol;
          
          // Make town symbols 80% the size of other symbols
          const iconScale = properties.type === 'town' ? properties.size * 0.6 : 
                          properties.type === 'epicentre' ? properties.size * 1.2 : // Make epicentre slightly larger
                          properties.type === 'office' ? properties.size * 1.0 : // Keep office size standard
                          properties.type === 'location' ? properties.size * 0.5 : // Make location markers smaller
                          properties.type === 'region' ? properties.size * 0.5 : // Make region markers same size as location
                          properties.type === 'capital' ? properties.size * 1.2 : // Make capital cities larger
                          properties.size;
          
          // For location type, use a square shape instead of an icon
          if (properties.type === 'location' || properties.type === 'region') { // Handle both location and region types
            vectorStyle = new Style({
              image: new RegularShape({
                points: 4, // Creates a square
                radius: 4, // Size of the square
                angle: Math.PI / 4, // Rotate 45 degrees to make it a square
                fill: new Fill({
                  color: 'rgba(0, 0, 0, 0.8)', // Dark gray/black color
                }),
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 0.8)', // White border for contrast
                  width: 1,
                }),
              }),
            });
          } else {
            vectorStyle = new Style({
              image: new Icon({
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                scale: iconScale,
                src: symbolIcons[iconSymbol],
              }),
            });
          }
        }
        return vectorStyle;
      }
      const features = points.map((row) => {
        let item = row;
        if (!Array.isArray(data)) {
          item = row.properties;
          [item.lon, item.lat] = row.geometry.coordinates;
        }

        let feature;

        let size = scale;
        if (size === 0.7) size = 0.6;
        if (size === 0.83) size = 0.9;

        // absolute scaling
        let exp = 0.5;
        if (scaleScaling === 'flannery') {
          exp = 0.5716;
        }
        let r = (1 / 3.14) ** exp * (10 * scale);

        if (scaleType === 'proportional') {
          const ratio = item[scaleColumn] / scaleDataMax;
          size = scale * ratio;
          r = ((item[scaleColumn] / scaleDataMax) / 3.14) ** exp * (10 * scale);
        }

        let iconStyle;
        if (symbol === 'circle') {
          feature = new Feature(new Point(fromLonLat([item.lon, item.lat])));
          feature.setProperties(item);
          // eslint-disable-next-line object-shorthand
          if (r > 0) {
            feature.setProperties({ style, size: r });
          } else {
            feature.setProperties({ style, size: r, opacity: 0 });
          }
          iconStyle = [styleFunction(feature)];
        } else {
          feature = new Feature(new Point(fromLonLat([item.lon, item.lat])));
          // Set the type property first
          if (item.type) {
            feature.set('type', item.type);
          }
          // Then set other properties
          feature.setProperties(item);
          // Set style with size
          feature.setProperties({ style: style, size: size });
          iconStyle = [styleFunction(feature)];
        }

        // Start with the base icon style
        const baseStyle = styleFunction(feature);
        iconStyle = [baseStyle];
        
        if (showLabels === true) {
          let label = (item[labelColumn]) ?? '';
          if (style.labelStyle.transform === 'uppercase') {
            label = String(label).toUpperCase();
          }

          const fill = new Fill({
            color: rgba(style.labelStyle.color),
          });

          let stroke = new Stroke({
            color: 'rgba(255,255,255,0.42)',
            width: 1.1,
          });
          if (style.labelStyle.showHalo === false) stroke = null;

          let textAlign = 'left';
          if (style.labelStyle.textAlign) textAlign = style.labelStyle.textAlign;
          let yPos = 1;
          let xPos = 7 + xOffset;
          if (scale < 0.5) {
            xPos = 4 + xOffset;
            yPos = 0;
          }

          if (scaleType === 'proportional') {
            textAlign = 'center';
            xPos = 0;
          }
          let str = String(label);
          if (labelStyle === 'population') yPos = -10;

          let fontWeight = 'normal';
          if (size === 0.9) fontWeight = '600';
          
          // Create a combined style with both icon and text
          if ((labelStyle !== 'population' || item[scaleColumn] >= 5) && item.type !== 'epicentre') {
            // Adjust font size based on location type
            const fontSize = item.type === 'town' ? 9 : 10;
            
            // Create a combined style that includes both the icon and the text
            // This ensures the text is only shown when the icon is visible
            const combinedStyle = new Style({
              // Use the same image as the base style
              image: baseStyle.getImage(),
              // Add text to the same style
              text: new Text({
                text: str,
                font: `${fontWeight} ${fontSize}px ${getFontFamily(item.type)}`,
                textAlign,
                offsetY: yPos - 0.5,
                offsetX: xPos,
                fill,
                stroke,
                priority: style.labelStyle.priority || 0, // Apply priority from style config
              }),
              // Set zIndex to ensure proper ordering
              zIndex: 1
            });
            
            // Replace the base style with the combined style
            iconStyle = [combinedStyle];
          }

          if (labelStyle === 'population' && item[scaleColumn] >= 5) {
            str = `${item[scaleColumn]}`;
            
            // If we already have a combined style, add the population text to it
            if (iconStyle.length > 0) {
              // Adjust font size based on location type
              const populationFontSize = item.type === 'town' 
                ? style.labelStyle.fontSize + 1  // Smaller for towns
                : style.labelStyle.fontSize + 2; // Regular size for other locations
              
              // Create a separate style for the population label
              const populationTextStyle = new Style({
                text: new Text({
                  text: numberFormatter(str),
                  font: `normal ${populationFontSize}px/1.07 ${getFontFamily(item.type)}`,
                  textAlign,
                  offsetY: yPos + 10,
                  offsetX: xPos,
                  fill,
                  stroke,
                }),
                // Set higher z-index for population labels
                zIndex: 2
              });
              
              // Add the population text style
              iconStyle.push(populationTextStyle);
            }
          }
        }

        feature.setStyle(iconStyle);
        return feature;
      });

      // Create the vector layer with decluttering enabled
      const vectorLayer = new OLVectorLayer({
        source: vector({ features }),
        renderers: ['SVG', 'VML', 'Canvas'],
        // Use the declutter setting from the layer configuration
        declutter: symbol === 'epicentre' ? false : true,
        // Use declutterMode 'obstacle-and-label' for combined styles
        // This ensures that when a point is hidden due to decluttering,
        // its label is also hidden
        declutterMode: 'obstacle-and-label'
      });
      
      return vectorLayer;
    } catch (error) {
    }
  };

  const setupTooltipElement = useCallback(() => {
    const tt = document.createElement('div');
    tt.classList.add('tooltip');
    tt.classList.add(`tooltip-${layerId}`);
    document.body.appendChild(tt);
    return tt;
  }, [layerId]);

  const setupPointerMoveListener = useCallback((popupOverlay, tooltipElement, vectorLayer) => {
    if (!vectorLayer || !map || !popupOverlay || !tooltipElement) {
      return () => {};
    }

    let thisId = null;
    let id = null;

    const handlePointerMove = (event) => {
      if (event.dragging) {
        return;
      }

      const pixel = map.getEventPixel(event.originalEvent);
      vectorLayer.getFeatures(pixel).then((vectorFeatures) => {
        const feature = vectorFeatures.length ? vectorFeatures[0] : undefined;
        let str = '';

        if (vectorFeatures.length) {
          id = feature.ol_uid;
          if ((id !== thisId) || (!thisId)) {
            thisId = id;
            
            if (tooltipsDateColumn) {
              const dateValue = feature.get(tooltipsDateColumn);
              if (dateValue) {
                const date = new Date(dateValue);
                const monthNames = [
                  'January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'
                ];
                const formattedDate = 
                  ('0' + date.getDate()).slice(-2) + ' ' + 
                  monthNames[date.getMonth()] + ' ' + 
                  date.getFullYear();
                str += `<div class="tooltip-date">${formattedDate}</div>`;
              }
            }

            if (tooltipsTitleColumn && feature.get(tooltipsTitleColumn)) {
              str += `<div class="tooltip-title">${feature.get(tooltipsTitleColumn)}</div>`;
            }

            if (tooltipsValueColumn && feature.get(tooltipsValueColumn)) {
              str += `<div class="tooltip-value">${feature.get(tooltipsValueColumn)}</div>`;
            }

            if (tooltipsValueLabel) {
              str += `<div class="tooltip-value-label">${tooltipsValueLabel}</div>`;
            }

            if (tooltipsValueColumnSecondary && feature.get(tooltipsValueColumnSecondary)) {
              str += `<div class="tooltip-value-column-secondary">${feature.get(tooltipsValueColumnSecondary)}</div>`;
            }

            if (tooltipsValueLabelSecondary) {
              str += `<div class="tooltip-value-label-secondary">${tooltipsValueLabelSecondary}</div>`;
            }

            if (str) {
              tooltipElement.innerHTML = str;
              popupOverlay.setPosition(event.coordinate);
            } else {
              popupOverlay.setPosition(undefined);
            }
          }
        } else {
          // Hide tooltip when no feature is found
          popupOverlay.setPosition(undefined);
          thisId = null;
        }
      }).catch(error => {
        popupOverlay.setPosition(undefined);
      });
    };

    // Add the event listener
    map.on('pointermove', handlePointerMove);

    // Return cleanup function
    return () => {
      map.un('pointermove', handlePointerMove);
    };
  }, [map, tooltipsDateColumn, tooltipsTitleColumn, tooltipsValueColumn, tooltipsValueLabel, 
      tooltipsValueColumnSecondary, tooltipsValueLabelSecondary]);

  useEffect(() => {
    const vectorLayer = createVectorLayer();
    if (vectorLayer && map) {
      vectorLayer.setZIndex(zIndex);
      vectorLayer.setOpacity(opacity);
      setSymbolLayer(vectorLayer);
      map.addLayer(vectorLayer);

      if (enableTooltips) {
        const tooltipElement = setupTooltipElement();
        const popupOverlay = new Overlay({
          element: tooltipElement,
          position: undefined,
          positioning: 'bottom-center',
          offset: [0, -10],
          stopEvent: false,
        });
        map.addOverlay(popupOverlay);
        
        // Pass the vectorLayer to setupPointerMoveListener
        const cleanup = setupPointerMoveListener(popupOverlay, tooltipElement, vectorLayer);
        
        return () => {
          cleanup();
          map.removeLayer(vectorLayer);
          if (tooltipElement && tooltipElement.parentNode) {
            tooltipElement.parentNode.removeChild(tooltipElement);
          }
          map.removeOverlay(popupOverlay);
        };
      }

      // If tooltips are not enabled, just return cleanup for the layer
      return () => {
        map.removeLayer(vectorLayer);
      };
    }
  }, [map, data, symbol, zIndex, opacity, showLabels, labelColumn, labelStyle, scale, 
      scaleType, scaleScaling, scaleColumn, scaleDataMin, scaleDataMax, style, 
      enableTooltips, setupPointerMoveListener, setupTooltipElement, fontVersion]); // Add fontVersion

  // Return null since we're managing the layer through useEffect
  return null;
};

export default SymbolLayer;